// frontend/src/hooks/patient/usePatientVisitSummary.js
import { useCallback, useEffect, useState } from "react";
import { getBackendURL } from "../../components/env";

/**
 * usePatientVisitSummary(appointmentId, schema, userId)
 *
 * Fetches the patient-safe visit summary from the backend and exposes
 * clinician-only mutation helpers.
 *
 *   GET  /api/patient/visit-summary/?appointment_id=&schema=&user_id=
 *   POST /api/patient/visit-summary/regenerate/   (doctor)
 *   POST /api/patient/visit-summary/update/       (doctor)
 *
 * Returns { summary, loading, error, regenerate, update } where
 * summary = { doc_summary, audio_summaries, hidden }.
 */
export function usePatientVisitSummary(appointmentId, schema, userId) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!appointmentId || !schema || !userId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${getBackendURL()}/api/patient/visit-summary/?appointment_id=${encodeURIComponent(
          appointmentId
        )}&schema=${encodeURIComponent(schema)}&user_id=${encodeURIComponent(
          userId
        )}`,
        { headers: { "Content-Type": "application/json" } }
      );
      if (!res.ok) throw new Error(`summary fetch failed (${res.status})`);
      setSummary(await res.json());
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [appointmentId, schema, userId]);

  useEffect(() => {
    load();
  }, [load]);

  const post = useCallback(
    async (path, body) => {
      const res = await fetch(
        `${getBackendURL()}/api/patient/visit-summary/${path}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            appointment_id: appointmentId,
            schema,
            user_id: userId,
            ...body,
          }),
        }
      );
      if (!res.ok) throw new Error("update failed");
      setSummary(await res.json());
    },
    [appointmentId, schema, userId]
  );

  const regenerate = useCallback(() => post("regenerate/", {}), [post]);
  const update = useCallback((body) => post("update/", body), [post]);

  return { summary, loading, error, regenerate, update };
}
